import React, { createContext, ReactNode, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { View } from "@amzn/stencil-react-components/layout";
import { userInfoQueryOptions } from "@/services/user-info/user-info.query";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { AmazonianInfo } from "@/services/user-info/user-info.types";
import { AppSkeleton } from "@/common/components/AppSkeleton/app-skeleton";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";

const UserInfoContext = createContext<AmazonianInfo | null>(null);

export const useUserInfo = (): AmazonianInfo => {
  const ctx = useContext(UserInfoContext);
  if (!ctx) {
    throw new Error("useUserInfo must be used within UserInfoProvider");
  }
  return ctx;
};

interface UserInfoProviderProps {
  children: ReactNode;
}

export const UserInfoProvider: React.FC<UserInfoProviderProps> = ({ children }) => {
  const { data, isLoading, isError } = useQuery<AmazonianInfo, Error>(userInfoQueryOptions);

  if (isLoading) {
    return <AppSkeleton />;
  }

  if (isError || !data) {
    return (
      <View padding="2rem">
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {getString("errors.somethingWentWrong")}
        </MessageBanner>
      </View>
    );
  }

  return <UserInfoContext.Provider value={data}>{children}</UserInfoContext.Provider>;
};
